import classNames from 'classnames'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import styles from './StatsGrid.module.css'

export interface StatsGridProps {
  title?: string
  subtitle?: string
  centered?: boolean
  theme?: ComponentTheme
  stats: {
    image?: any
    largeText: string
    title: string
    text: string
    largeTitle?: boolean
  }[]
  sideTitle?: string
  sideSubTitle?: string
  sideText?: string
  sideImage?: any
  statsAsColumns?: boolean
  statsRight?: boolean
  verticalPadding?: boolean
  testID?: string
  noPaddingBottom?: boolean // unused but kept for backward compatibility
  moduleId?: string
  borderBottom?: boolean
  borderTop?: boolean
  displayAsGridOnMobile?: boolean
}

const StatsGrid = ({
  title,
  subtitle,
  stats,
  sideTitle,
  sideSubTitle,
  sideText,
  sideImage,
  statsAsColumns = false,
  statsRight = false,
  centered = false,
  theme,
  verticalPadding = true,
  noPaddingBottom = false, // unused but kept for backward compatibility
  testID,
  moduleId,
  borderBottom = false,
  borderTop = false,
  displayAsGridOnMobile = false,
}: StatsGridProps) => {
  const hasSideContent = sideTitle || sideSubTitle || sideText || sideImage

  return (
    <AnimateIntoView type={AnimationType.fadeInFromBottom} delay={0.2}>
      <Fixture
        verticalPadding={verticalPadding}
        theme={theme}
        noPaddingBottom={noPaddingBottom}
        id={moduleId}
      >
        <Container>
          <div
            className={classNames(styles.base, {
              [styles.centered]: centered,
              [styles.secondary]: theme === ComponentTheme.secondary,
              [styles.borderBottom]: borderBottom,
              [styles.borderTop]: borderTop,
            })}
            data-testid={testID}
          >
            {(title || subtitle) && (
              <div className={styles.header}>
                {title && (
                  <Text
                    Tag="h2"
                    size={TextSizes.secondaryTitleLrg}
                    className={styles.title}
                  >
                    {title}
                  </Text>
                )}
                {subtitle && (
                  <Text
                    Tag="p"
                    size={TextSizes.bodyMed}
                    className={styles.subtitle}
                  >
                    {subtitle}
                  </Text>
                )}
              </div>
            )}

            <div
              className={classNames(styles.content, {
                [styles.contentReversed]: !statsRight && hasSideContent,
                [styles.contentWithSide]: hasSideContent,
              })}
            >
              {hasSideContent && (
                <div className={styles.sideContent}>
                  {sideTitle && (
                    <Text
                      Tag="h3"
                      size={TextSizes.primaryTitleMed}
                      className={styles.sideTitle}
                    >
                      {sideTitle}
                    </Text>
                  )}
                  {sideImage && (
                    <div className={styles.sideImageWrapper}>
                      <Image {...sideImage} objectFit="contain" />
                    </div>
                  )}

                  {sideSubTitle && (
                    <Text
                      Tag="h4"
                      size={TextSizes.primaryTitleMed}
                      className={styles.sideSubTitle}
                    >
                      {sideSubTitle}
                    </Text>
                  )}
                  {sideText && (
                    <Text
                      Tag="p"
                      size={TextSizes.bodyMed}
                      className={styles.sideText}
                    >
                      {sideText}
                    </Text>
                  )}
                </div>
              )}

              <div
                className={classNames(styles.statsContainer, {
                  [styles.statsColumns]: statsAsColumns,
                  [styles.statsRows]: !statsAsColumns,
                  [styles.statsWithSide]: hasSideContent,
                  [styles.statsNoSide]: !hasSideContent,
                  [styles.displayAsGridOnMobile]: displayAsGridOnMobile,
                })}
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={classNames(styles.statItem, {
                      [styles.statItemColumn]: statsAsColumns,
                      [styles.statItemRow]: !statsAsColumns,
                      [styles.statItemWithSide]:
                        hasSideContent && !statsAsColumns,
                      [styles.statItemNoSide]:
                        !hasSideContent && !statsAsColumns,
                    })}
                  >
                    <div className={styles.statContent}>
                      {stat.image && (
                        <div className={styles.statImage}>
                          <Image {...stat.image} objectFit="contain" />
                        </div>
                      )}
                      <Text
                        Tag="div"
                        size={TextSizes.secondaryTitleLrg}
                        className={classNames(styles.statLargeText, {
                          [styles.smallerLargeText]: stat.image,
                        })}
                      >
                        {stat.largeText}
                      </Text>
                      <Text
                        Tag="h3"
                        size={
                          stat?.largeTitle
                            ? TextSizes.primaryTitleMed
                            : TextSizes.bodySml
                        }
                        className={classNames(styles.statTitle, {
                          [styles.statTitleLarge]: stat.largeTitle,
                        })}
                      >
                        {stat.title}
                      </Text>
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={styles.statText}
                      >
                        {stat.text}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Fixture>
    </AnimateIntoView>
  )
}

export default StatsGrid
